import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  getOnDemandSigningContainerById,
  getOnDemandSigningContainerRecipients,
  deleteOnDemandSigningContainer,
  exportOnDemandSigningContainerCsv,
  queueOnDemandSignedPdfZipExport,
  getOnDemandSignedPdfZipExportJob,
  getLatestCompletedOnDemandSignedPdfZipExport,
  getActiveOnDemandSignedPdfZipExport,
} from "../api/documents";
import { formatForDisplay } from "../utils/gmtUtils";
import { getSignedUrl } from "../utils/signedUrl";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "./common/BreadCrumb";
import { Button, Card } from "./ui";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const PAGE_SIZE_OPTIONS = [50, 100, 200];

/** Sign request statuses (linked to each on-demand recipient). */
const signRequestStatusStyles = {
  PENDING: "bg-slate-100 text-slate-800",
  INITIATED: "bg-slate-100 text-slate-800",
  IN_PROGRESS: "bg-amber-100 text-amber-800",
  COMPLETED: "bg-emerald-100 text-emerald-800",
  DECLINED: "bg-red-100 text-red-800",
  EXPIRED: "bg-gray-200 text-gray-800",
  ARCHIVED: "bg-gray-100 text-gray-600",
};

const signRequestStatusLabels = {
  PENDING: "Pending",
  INITIATED: "Initiated",
  IN_PROGRESS: "In progress",
  COMPLETED: "Completed",
  DECLINED: "Declined",
  EXPIRED: "Expired",
  ARCHIVED: "Archived",
};

/** Stored sign links are path-only (e.g. /sign/:requestId/:contactId); resolve in the browser. */
function resolveRecipientSignHref(stored) {
  if (!stored || typeof stored !== "string") return "";
  const trimmed = stored.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

async function copyClipboardText(text, successMessage = "Copied to clipboard") {
  if (!text) {
    toast.error("Nothing to copy");
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch {
    toast.error("Could not copy");
  }
}

function sanitizeOnDemandDownloadPart(s) {
  if (s == null) return "";
  return String(s)
    .replace(/[<>:"/\\|?\n\r]+/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 100);
}

/** Signed-PDF download filename stem: SI.No_Name */
function onDemandSignedPdfDownloadStem(r) {
  const si = sanitizeOnDemandDownloadPart(r.siNo);
  const nm = sanitizeOnDemandDownloadPart(r.name);
  if (si && nm) return `${si}_${nm}`;
  if (nm) return nm;
  if (si) return si;
  return "signed-document";
}

function buildVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = new Set([1, totalPages, currentPage]);
  for (let d = 1; d <= 2; d++) {
    pages.add(Math.max(1, currentPage - d));
    pages.add(Math.min(totalPages, currentPage + d));
  }
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push("ellipsis");
    out.push(p);
    prev = p;
  }
  return out;
}

const OnDemandSigningContainerDetailView = ({
  containerId,
  onBack,
  onDeleted,
}) => {
  const { setHeaderContent } = usePageHeader();
  const [container, setContainer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [recipientPage, setRecipientPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [pageInput, setPageInput] = useState("1");
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [recipientRows, setRecipientRows] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientTotal, setRecipientTotal] = useState(0);
  const [recipientTotalPages, setRecipientTotalPages] = useState(1);
  const [recipientSearchInput, setRecipientSearchInput] = useState("");
  const [debouncedRecipientSearch, setDebouncedRecipientSearch] = useState("");
  const [importPoll, setImportPoll] = useState(0);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [signedPdfDownloadId, setSignedPdfDownloadId] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [zipExporting, setZipExporting] = useState(false);
  const [zipExportJobId, setZipExportJobId] = useState("");
  const [latestCompletedZipJob, setLatestCompletedZipJob] = useState(null);
  const [downloadingLatestZip, setDownloadingLatestZip] = useState(false);
  const [activeZipJob, setActiveZipJob] = useState(null);

  const containerImportStatus = container?.importStatus || "ready";

  useEffect(() => {
    setHeaderContent(
      <div>
        <BreadCrumb
          items={[
            { label: "Dashboard", href: "/dashboard" },
            {
              label: "On-demand Signing",
              href: "/dashboard/ondemand-signing",
            },
            {
              label: loading ? "…" : container?.containerName || "Container",
            },
          ]}
          className="mb-2"
        />
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
          {loading ? "Loading…" : container?.containerName || "Container"}
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl mt-1">
          Recipients, signing status, and links for this container.
        </p>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent, loading, container?.containerName]);

  useLayoutEffect(() => {
    if (!containerId) return;
    setRecipientPage(1);
    setPageInput("1");
    setShowDeleteModal(false);
    setRecipientSearchInput("");
    setDebouncedRecipientSearch("");
    setImportPoll(0);
    setZipExporting(false);
    setZipExportJobId("");
    setLatestCompletedZipJob(null);
    setDownloadingLatestZip(false);
    setActiveZipJob(null);
  }, [containerId]);

  useEffect(() => {
    if (!containerId) return;
    let cancelled = false;
    (async () => {
      try {
        const data =
          await getLatestCompletedOnDemandSignedPdfZipExport(containerId);
        if (!cancelled) {
          setLatestCompletedZipJob(data?.job || null);
        }
      } catch {
        if (!cancelled) setLatestCompletedZipJob(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [containerId]);

  useEffect(() => {
    if (!containerId) return;
    let cancelled = false;

    const loadActiveJob = async () => {
      try {
        const data = await getActiveOnDemandSignedPdfZipExport(containerId);
        if (!cancelled) setActiveZipJob(data?.job || null);
      } catch {
        if (!cancelled) setActiveZipJob(null);
      }
    };

    loadActiveJob();
    const id = setInterval(loadActiveJob, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [containerId]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedRecipientSearch(recipientSearchInput.trim());
    }, 320);
    return () => clearTimeout(t);
  }, [recipientSearchInput]);

  useEffect(() => {
    setRecipientPage(1);
    setPageInput("1");
  }, [debouncedRecipientSearch]);

  useEffect(() => {
    if (!containerId || containerImportStatus !== "importing") return;
    const id = setInterval(() => {
      setImportPoll((p) => p + 1);
    }, 3000);
    return () => clearInterval(id);
  }, [containerId, containerImportStatus]);

  useEffect(() => {
    if (!zipExportJobId) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const data = await getOnDemandSignedPdfZipExportJob(zipExportJobId);
        if (cancelled) return;
        const job = data?.job;
        if (!job) return;

        if (job.status === "COMPLETED" && job.zipPath) {
          const stem = job.downloadFileStem || "signed_pdfs";
          const url = await getSignedUrl(job.zipPath, stem);
          if (cancelled) return;
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          requestAnimationFrame(() => {
            if (a.parentNode) a.parentNode.removeChild(a);
          });
          toast.success("ZIP download started");
          setLatestCompletedZipJob({
            id: job.id,
            status: job.status,
            zipPath: job.zipPath,
            totalSignRequests: job.totalSignRequests,
            processedSignRequests: job.processedSignRequests,
            completedAt: job.completedAt,
            createdAt: job.createdAt,
            downloadFileStem: stem,
          });
          setZipExportJobId("");
          setZipExporting(false);
          setActiveZipJob(null);
          return;
        }

        if (job.status === "FAILED") {
          toast.error(job.errorMessage || "Signed PDF ZIP export failed");
          setZipExportJobId("");
          setZipExporting(false);
          setActiveZipJob(null);
        }
      } catch (err) {
        if (!cancelled) {
          const msg =
            err?.message ||
            err?.response?.data?.message ||
            "Failed to check export job";
          toast.error(msg);
          setZipExportJobId("");
          setZipExporting(false);
        }
      }
    };

    poll();
    const id = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [zipExportJobId]);

  useEffect(() => {
    if (!containerId) {
      setContainer(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const isInitialPoll = importPoll === 0;
      if (isInitialPoll) {
        setLoading(true);
        setContainer(null);
      }
      try {
        const data = await getOnDemandSigningContainerById(containerId);
        if (!cancelled) {
          setContainer(data);
        }
      } catch (err) {
        if (!cancelled) {
          const msg =
            typeof err === "object" && err?.message
              ? err.message
              : "Failed to load container";
          toast.error(msg);
          onBack();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [containerId, importPoll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDetailsExpanded(false);
  }, [containerId]);

  useEffect(() => {
    setPageInput(String(recipientPage));
  }, [recipientPage]);

  useEffect(() => {
    if (!containerId) {
      setRecipientRows([]);
      setRecipientTotal(0);
      setRecipientTotalPages(1);
      return;
    }

    let cancelled = false;
    (async () => {
      setRecipientsLoading(true);
      try {
        const data = await getOnDemandSigningContainerRecipients(containerId, {
          page: recipientPage,
          limit: pageSize,
          search: debouncedRecipientSearch,
          sortBy,
          sortOrder,
        });
        if (cancelled) return;
        setRecipientRows(data.recipients || []);
        setRecipientTotal(Number(data.total) || 0);
        setRecipientTotalPages(Math.max(1, Number(data.totalPages) || 1));
      } catch (err) {
        if (!cancelled) {
          const msg =
            typeof err === "object" && err?.message
              ? err.message
              : "Failed to load recipients";
          toast.error(msg);
          setRecipientRows([]);
          setRecipientTotal(0);
          setRecipientTotalPages(1);
        }
      } finally {
        if (!cancelled) setRecipientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    containerId,
    recipientPage,
    pageSize,
    debouncedRecipientSearch,
    importPoll,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    if (recipientPage > recipientTotalPages) {
      setRecipientPage(recipientTotalPages);
    }
  }, [recipientPage, recipientTotalPages]);

  const visiblePages = useMemo(
    () => buildVisiblePageNumbers(recipientPage, recipientTotalPages),
    [recipientPage, recipientTotalPages],
  );

  const creatorLabel = useMemo(() => {
    if (!container?.userId) return "—";
    const u = container.userId;
    if (typeof u === "object") {
      return u.name?.trim() || u.email || "—";
    }
    return "—";
  }, [container]);

  const containerStatusStats = useMemo(() => {
    const total = Number(container?.recipientCount ?? recipientTotal) || 0;
    const signed = Number(container?.signedCount) || 0;
    const declined = Number(container?.declinedCount) || 0;
    const pending =
      container?.pendingCount == null
        ? Math.max(0, total - signed - declined)
        : Number(container.pendingCount) || 0;

    return [
      {
        label: "Total recipients",
        value: total,
        className: "border-gray-200 bg-gray-50 text-gray-900",
      },
      {
        label: "Signed",
        value: signed,
        className: "border-emerald-100 bg-emerald-50 text-emerald-900",
      },
      {
        label: "Pending",
        value: pending,
        className: "border-amber-100 bg-amber-50 text-amber-900",
      },
      {
        label: "Declined",
        value: declined,
        className: "border-red-100 bg-red-50 text-red-900",
      },
    ];
  }, [container, recipientTotal]);

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(recipientTotalPages, p));
    setRecipientPage(next);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const n = parseInt(pageInput, 10);
    if (!Number.isFinite(n)) {
      setPageInput(String(recipientPage));
      return;
    }
    goToPage(n);
  };

  const handlePageSizeChange = (e) => {
    const next = Number(e.target.value);
    setPageSize(next);
    setRecipientPage(1);
    setPageInput("1");
  };

  const clearRecipientSearch = () => {
    setRecipientSearchInput("");
    setDebouncedRecipientSearch("");
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setRecipientPage(1);
    setPageInput("1");
  };

  const recipientStart = (recipientPage - 1) * pageSize;
  const recipientEndExclusive = Math.min(
    recipientPage * pageSize,
    recipientTotal,
  );

  const handleOpenDeleteModal = () => {
    if (!containerId || !container || deleteSubmitting) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!containerId) return;
    try {
      setDeleteSubmitting(true);
      await deleteOnDemandSigningContainer(containerId);
      toast.success("Container removed");
      setShowDeleteModal(false);
      onDeleted?.();
      onBack();
    } catch (err) {
      const msg =
        typeof err === "object" && err?.message
          ? err.message
          : "Failed to remove container";
      toast.error(msg);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleExportCsv = async () => {
    if (!containerId || !container) return;
    try {
      setExportingCsv(true);
      await exportOnDemandSigningContainerCsv(containerId, {
        containerName: container.containerName,
      });
      toast.success("Export downloaded");
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Could not export recipients";
      toast.error(msg);
    } finally {
      setExportingCsv(false);
    }
  };

  const handleDownloadSignedPdf = async (r) => {
    if (!r.signedDocumentKey) return;
    try {
      setSignedPdfDownloadId(r._id);
      const stem = onDemandSignedPdfDownloadStem(r);
      const url = await getSignedUrl(r.signedDocumentKey, stem);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      requestAnimationFrame(() => {
        document.body.removeChild(a);
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not download document";
      toast.error(msg);
    } finally {
      setSignedPdfDownloadId(null);
    }
  };

  const handleExportSignedZip = async () => {
    if (
      !containerId ||
      !container ||
      zipExporting ||
      (activeZipJob &&
        (activeZipJob.status === "PENDING" ||
          activeZipJob.status === "PROCESSING"))
    ) {
      return;
    }
    try {
      setZipExporting(true);
      const data = await queueOnDemandSignedPdfZipExport(containerId);
      const jobId = data?.job?.id;
      if (!jobId) {
        throw new Error("Could not queue ZIP export");
      }
      setZipExportJobId(jobId);
      setActiveZipJob({
        id: jobId,
        status: data?.job?.status || "PENDING",
        totalSignRequests: data?.job?.totalSignRequests || 0,
        processedSignRequests: data?.job?.processedSignRequests || 0,
      });
      toast.success("Preparing ZIP in background...");
    } catch (err) {
      const msg =
        err?.message || err?.response?.data?.message || "Could not export ZIP";
      toast.error(msg);
      if (
        err?.job &&
        (err.job.status === "PENDING" || err.job.status === "PROCESSING")
      ) {
        setActiveZipJob(err.job);
      }
      setZipExporting(false);
    }
  };

  const hasActiveZipJob =
    activeZipJob &&
    (activeZipJob.status === "PENDING" || activeZipJob.status === "PROCESSING");

  const handleDownloadLatestCompletedZip = async () => {
    if (!latestCompletedZipJob?.zipPath || downloadingLatestZip) return;
    try {
      setDownloadingLatestZip(true);
      const stem = latestCompletedZipJob.downloadFileStem || "signed_pdfs";
      const url = await getSignedUrl(latestCompletedZipJob.zipPath, stem);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      requestAnimationFrame(() => {
        if (a.parentNode) a.parentNode.removeChild(a);
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not download ZIP";
      toast.error(msg);
    } finally {
      setDownloadingLatestZip(false);
    }
  };

  return (
    <>
      <div className="bg-white overflow-hidden">
        <div className="px-4 py-6">
          <div className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4 min-w-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  leftIcon={ChevronLeftIcon}
                  className="w-fit shrink-0 text-gray-700 -ml-2"
                >
                  Back to containers
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 lg:justify-end shrink-0">
                {!loading && container ? (
                  <>
                    {container.ownerPortalUrl ? (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            copyClipboardText(
                              container.ownerPortalUrl,
                              "Owner portal link copied to clipboard",
                            )
                          }
                          className="sm:hidden p-2 rounded-lg text-primary hover:bg-primary/10"
                          aria-label="Copy owner portal link"
                        >
                          <ClipboardDocumentIcon className="h-5 w-5" />
                        </button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          leftIcon={ClipboardDocumentIcon}
                          onClick={() =>
                            copyClipboardText(
                              container.ownerPortalUrl,
                              "Owner portal link copied to clipboard",
                            )
                          }
                          className="hidden sm:inline-flex"
                        >
                          Copy owner link
                        </Button>
                      </>
                    ) : null}
                    <button
                      type="button"
                      onClick={handleOpenDeleteModal}
                      disabled={deleteSubmitting}
                      className="sm:hidden p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                      aria-label="Delete container"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      leftIcon={TrashIcon}
                      onClick={handleOpenDeleteModal}
                      disabled={deleteSubmitting}
                      className="hidden sm:inline-flex"
                    >
                      Delete container
                    </Button>
                  </>
                ) : null}
              </div>
            </div>

            {!loading && container?.importStatus === "importing" ? (
              <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Importing recipients in the background. This page refreshes
                automatically every few seconds.
              </div>
            ) : null}
            {!loading && container?.importStatus === "failed" ? (
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
                Import failed.
                {container.importError
                  ? ` ${container.importError}`
                  : " Remove this container and try again."}
              </div>
            ) : null}

            {loading && (
              <Card
                padding="none"
                className="overflow-hidden border border-gray-100"
              >
                <div className="space-y-3 animate-pulse p-6">
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-40 bg-gray-100 rounded-lg" />
                </div>
              </Card>
            )}

            {!loading && container && (
              <>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {containerStatusStats.map((stat) => (
                    <div
                      key={stat.label}
                      className={`rounded-lg border px-4 py-3 ${stat.className}`}
                    >
                      <p className="text-xs font-medium uppercase tracking-wide opacity-75">
                        {stat.label}
                      </p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums">
                        {stat.value.toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                <Card
                  padding="none"
                  className="overflow-hidden border border-gray-100"
                >
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between bg-gray-50/60">
                    <div className="flex flex-wrap justify-between w-full items-center gap-2">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          leftIcon={ArrowDownTrayIcon}
                          onClick={handleExportCsv}
                          disabled={
                            exportingCsv ||
                            containerImportStatus === "importing"
                          }
                        >
                          {exportingCsv ? "Exporting…" : "Export all CSV"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          leftIcon={ArrowDownTrayIcon}
                          onClick={handleExportSignedZip}
                          disabled={
                            zipExporting ||
                            containerImportStatus === "importing" ||
                            hasActiveZipJob
                          }
                        >
                          {zipExporting
                            ? "Preparing ZIP…"
                            : hasActiveZipJob
                              ? "Export in progress…"
                              : "Export signed PDFs ZIP"}
                        </Button>
                        {latestCompletedZipJob?.zipPath ? (
                          <Button
                            type="button"
                            variant="text"
                            size="sm"
                            leftIcon={ArrowDownTrayIcon}
                            onClick={handleDownloadLatestCompletedZip}
                            disabled={downloadingLatestZip}
                          >
                            {downloadingLatestZip
                              ? "Downloading ZIP…"
                              : "Download latest ZIP"}
                          </Button>
                        ) : null}
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="whitespace-nowrap text-gray-500">
                          Rows per page
                        </span>
                        <select
                          value={pageSize}
                          onChange={handlePageSizeChange}
                          className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        >
                          {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                  <div className="px-4 sm:px-6 py-4">
                    <div className="relative">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        value={recipientSearchInput}
                        onChange={(e) =>
                          setRecipientSearchInput(e.target.value)
                        }
                        placeholder="Search by name, email, unit, department…"
                        autoComplete="off"
                        className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                        aria-label="Search recipients"
                      />
                      {recipientSearchInput ? (
                        <button
                          type="button"
                          onClick={clearRecipientSearch}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          aria-label="Clear search"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="relative border-t border-gray-100">
                    {recipientsLoading && (
                      <div className="absolute inset-0 bg-white/70 z-[2] flex items-center justify-center backdrop-blur-[1px]">
                        <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary border-t-transparent" />
                      </div>
                    )}
                    <div className="max-h-[min(58vh,560px)] overflow-auto overscroll-contain">
                      <table className="min-w-full text-left text-sm leading-snug table-fixed">
                        <thead className="sticky top-0 z-[1] border-b border-gray-200 bg-gray-50/95 backdrop-blur-sm">
                          <tr className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            <th className="!w-[4%] px-3 py-2.5 text-left ">
                              #
                            </th>
                            <th className="px-3 py-2.5 w-[9%]">
                              <button
                                type="button"
                                onClick={() => handleSort("siNo")}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                SI.No
                                {sortBy === "siNo" &&
                                  (sortOrder === "asc" ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                  ))}
                              </button>
                            </th>
                            <th className="px-3 py-2.5 w-[18%]">
                              <button
                                type="button"
                                onClick={() => handleSort("name")}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                Name
                                {sortBy === "name" &&
                                  (sortOrder === "asc" ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                  ))}
                              </button>
                            </th>
                            <th className="px-3 py-2.5 w-[20%]">
                              <button
                                type="button"
                                onClick={() => handleSort("email")}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                Email
                                {sortBy === "email" &&
                                  (sortOrder === "asc" ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                  ))}
                              </button>
                            </th>
                            <th className="px-3 py-2.5 w-[12%]">
                              <button
                                type="button"
                                onClick={() => handleSort("unit")}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                Unit
                                {sortBy === "unit" &&
                                  (sortOrder === "asc" ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                  ))}
                              </button>
                            </th>
                            <th className="px-3 py-2.5 w-[12%]">
                              <button
                                type="button"
                                onClick={() => handleSort("department")}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                Department
                                {sortBy === "department" &&
                                  (sortOrder === "asc" ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                  ))}
                              </button>
                            </th>
                            <th className="px-3 py-2.5 w-[12%]">
                              <button
                                type="button"
                                onClick={() => handleSort("signRequestStatus")}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                Status
                                {sortBy === "signRequestStatus" &&
                                  (sortOrder === "asc" ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                  ))}
                              </button>
                            </th>
                            <th className="px-3 py-2.5 w-[11%]">
                              <button
                                type="button"
                                onClick={() => handleSort("signedOn")}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                Signed on
                                {sortBy === "signedOn" &&
                                  (sortOrder === "asc" ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                  ))}
                              </button>
                            </th>
                            <th className="px-3 py-2.5 w-[9%]">Signed PDF</th>
                            <th className="px-3 py-2.5 w-[10%]">Sign link</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-gray-800">
                          {recipientRows.length === 0 && !recipientsLoading ? (
                            <tr>
                              <td
                                colSpan={10}
                                className="px-4 py-10 text-center text-sm text-gray-500"
                              >
                                {debouncedRecipientSearch ? (
                                  <div className="space-y-1">
                                    <p className="font-medium text-gray-700">
                                      No matches
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Try a different name or email, or clear
                                      the search.
                                    </p>
                                    <button
                                      type="button"
                                      onClick={clearRecipientSearch}
                                      className="mt-2 text-xs font-medium text-primary hover:underline"
                                    >
                                      Clear search
                                    </button>
                                  </div>
                                ) : (
                                  "No recipients in this container yet."
                                )}
                              </td>
                            </tr>
                          ) : (
                            recipientRows.map((r, idx) => (
                              <tr
                                key={r._id}
                                className={
                                  idx % 2 === 1
                                    ? "bg-gray-50/40 hover:bg-primary/[0.04]"
                                    : "hover:bg-primary/[0.04]"
                                }
                              >
                                <td className="px-3 py-2.5 text-left text-xs text-gray-500 tabular-nums align-top">
                                  {(r.rowIndex + 1).toLocaleString()}
                                </td>
                                <td
                                  className="px-3 py-2.5 align-top text-gray-700 truncate text-xs tabular-nums"
                                  title={r.siNo || ""}
                                >
                                  {r.siNo || "—"}
                                </td>
                                <td
                                  className="px-3 py-2.5 align-top font-medium text-gray-900 truncate"
                                  title={r.name}
                                >
                                  {r.name}
                                </td>
                                <td
                                  className="px-3 py-2.5 align-top text-gray-700 truncate"
                                  title={r.email}
                                >
                                  {r.email}
                                </td>
                                <td
                                  className="px-3 py-2.5 align-top text-gray-700 truncate text-xs"
                                  title={r.unit || ""}
                                >
                                  {r.unit || "—"}
                                </td>
                                <td
                                  className="px-3 py-2.5 align-top text-gray-700 truncate text-xs"
                                  title={r.department || ""}
                                >
                                  {r.department || "—"}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  {r.signRequestStatus ? (
                                    <span
                                      className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                        signRequestStatusStyles[
                                          r.signRequestStatus
                                        ] || "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      <span className="truncate">
                                        {signRequestStatusLabels[
                                          r.signRequestStatus
                                        ] ||
                                          String(r.signRequestStatus).replace(
                                            /_/g,
                                            " ",
                                          )}
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 align-top text-xs text-gray-600 whitespace-nowrap">
                                  {r.signedOn ? (
                                    formatForDisplay(
                                      r.signedOn,
                                      "MMM D, YYYY h:mm a",
                                    )
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 align-top whitespace-nowrap">
                                  {r.signedDocumentKey ? (
                                    <button
                                      type="button"
                                      onClick={() => handleDownloadSignedPdf(r)}
                                      disabled={signedPdfDownloadId === r._id}
                                      className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary hover:underline disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                      <ArrowDownTrayIcon className="h-5 w-5 shrink-0 opacity-90" />
                                      {signedPdfDownloadId === r._id ? "…" : ""}
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  {r.signUrl ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyClipboardText(
                                          resolveRecipientSignHref(r.signUrl),
                                          "Signing link copied to clipboard",
                                        )
                                      }
                                      className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary hover:underline"
                                    >
                                      <ClipboardDocumentIcon className="h-5 w-5 shrink-0 opacity-90" />
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {recipientTotal > 0 && (
                    <div className="px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/50 space-y-2">
                      <p className="text-sm text-gray-600">
                        Showing{" "}
                        <span className="font-semibold text-gray-800 tabular-nums">
                          {recipientTotal === 0
                            ? 0
                            : (recipientStart + 1).toLocaleString()}
                        </span>
                        –
                        <span className="font-semibold text-gray-800 tabular-nums">
                          {recipientEndExclusive.toLocaleString()}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-gray-800 tabular-nums">
                          {recipientTotal.toLocaleString()}
                        </span>
                        {debouncedRecipientSearch ? (
                          <span className="text-gray-500"> (filtered)</span>
                        ) : null}
                      </p>

                      {recipientTotalPages > 1 && (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                          <div className="flex flex-wrap items-center gap-1">
                            <button
                              type="button"
                              onClick={() => goToPage(1)}
                              disabled={recipientPage <= 1}
                              className="px-2 py-1 rounded-md border border-gray-200 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none shadow-sm"
                            >
                              First
                            </button>
                            <button
                              type="button"
                              onClick={() => goToPage(recipientPage - 1)}
                              disabled={recipientPage <= 1}
                              className="p-1 rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none shadow-sm"
                              aria-label="Previous page"
                            >
                              <ChevronLeftIcon className="h-4 w-4" />
                            </button>
                            <div className="flex flex-wrap items-center gap-0.5 mx-0.5">
                              {visiblePages.map((item, i) =>
                                item === "ellipsis" ? (
                                  <span
                                    key={`e-${i}`}
                                    className="px-0.5 text-gray-400 text-xs"
                                  >
                                    …
                                  </span>
                                ) : (
                                  <button
                                    key={item}
                                    type="button"
                                    onClick={() => goToPage(item)}
                                    className={`min-w-[1.75rem] px-1.5 py-1 rounded-md text-xs font-medium shadow-sm ${
                                      recipientPage === item
                                        ? "bg-primary text-white border border-primary"
                                        : "border border-gray-200 text-gray-700 bg-white hover:bg-gray-50"
                                    }`}
                                  >
                                    {item.toLocaleString()}
                                  </button>
                                ),
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => goToPage(recipientPage + 1)}
                              disabled={recipientPage >= recipientTotalPages}
                              className="p-1 rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none shadow-sm"
                              aria-label="Next page"
                            >
                              <ChevronRightIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => goToPage(recipientTotalPages)}
                              disabled={recipientPage >= recipientTotalPages}
                              className="px-2 py-1 rounded-md border border-gray-200 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none shadow-sm"
                            >
                              Last
                            </button>
                          </div>

                          <form
                            onSubmit={handlePageInputSubmit}
                            className="flex items-center gap-2 text-xs text-gray-600"
                          >
                            <span className="whitespace-nowrap">Go to</span>
                            <input
                              type="number"
                              min={1}
                              max={recipientTotalPages}
                              value={pageInput}
                              onChange={(e) => setPageInput(e.target.value)}
                              className="w-14 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-900 font-medium bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                            <span className="text-gray-500 whitespace-nowrap">
                              / {recipientTotalPages.toLocaleString()}
                            </span>
                            <Button type="submit" variant="outline" size="sm">
                              Go
                            </Button>
                          </form>
                        </div>
                      )}
                    </div>
                  )}
                </Card>

                <Card
                  padding="none"
                  className="overflow-hidden border border-gray-100"
                >
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-2 px-4 sm:px-6 py-3 text-left text-sm font-semibold text-gray-900 bg-gray-50/80 hover:bg-gray-100/80 transition-colors border-b border-gray-100"
                    onClick={() => setDetailsExpanded((v) => !v)}
                    aria-expanded={detailsExpanded}
                  >
                    <span>Container details</span>
                    {detailsExpanded ? (
                      <ChevronDownIcon className="h-4 w-4 text-gray-500 shrink-0" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 text-gray-500 shrink-0" />
                    )}
                  </button>
                  {detailsExpanded && (
                    <div className="px-4 sm:px-6 py-5">
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                        <div>
                          <dt className="text-gray-500 font-medium text-xs uppercase tracking-wide">
                            Owner email
                          </dt>
                          <dd className="mt-1 text-gray-900 text-sm">
                            <a
                              href={`mailto:${container.ownerEmail}`}
                              className="text-primary hover:underline break-all"
                            >
                              {container.ownerEmail}
                            </a>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 font-medium text-xs uppercase tracking-wide">
                            Template
                          </dt>
                          <dd className="mt-1 text-gray-900 font-medium text-sm">
                            {container.templateId?.name || "—"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 font-medium text-xs uppercase tracking-wide">
                            Recipients
                          </dt>
                          <dd className="mt-1 text-gray-900 tabular-nums text-sm">
                            {Number(
                              container.recipientCount ?? recipientTotal,
                            ).toLocaleString()}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 font-medium text-xs uppercase tracking-wide">
                            Created by
                          </dt>
                          <dd className="mt-1 text-gray-900 break-all text-sm">
                            {creatorLabel}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 font-medium text-xs uppercase tracking-wide">
                            Created
                          </dt>
                          <dd className="mt-1 text-gray-900 text-sm">
                            {formatForDisplay(
                              container.createdAt,
                              "MMM D, YYYY h:mm a",
                            )}
                          </dd>
                        </div>
                        <div className="sm:col-span-2">
                          <dt className="text-gray-500 font-medium text-xs uppercase tracking-wide">
                            Last updated
                          </dt>
                          <dd className="mt-1 text-gray-900 text-sm">
                            {formatForDisplay(
                              container.updatedAt,
                              "MMM D, YYYY h:mm a",
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          if (!deleteSubmitting) setShowDeleteModal(false);
        }}
        onConfirm={handleDeleteConfirm}
        title="Remove container"
        message="This only hides the container from your on-demand list. Linked sign requests and documents stay in Documents and are not deleted."
        documentName={container?.containerName || ""}
        nameLabel="Container"
        isLoading={deleteSubmitting}
      />
    </>
  );
};

export default OnDemandSigningContainerDetailView;
